import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import {
  CardComponent,
  ConfirmationModalService,
  confirmationModalServiceInjectionToken,
  EFieldType,
  EPageLayout,
  ESeverity,
  ETabNavigationMode,
  IConfirmationModalService,
  IInputGroupOptions,
  InputGroupComponent,
  ITab,
  IToastService,
  ModalService,
  modalServiceInjectionToken,
  PageLayoutComponent,
  TabNavigationComponent,
  TabNavigationStateService,
  tabNavigationStateServiceInjectionToken,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { SkeletonModule } from "primeng/skeleton";
import { DialogService } from 'primeng/dynamicdialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Button, ButtonModule } from "primeng/button";
import { Subscription } from 'rxjs';
import { NgClass, NgOptimizedImage } from "@angular/common";
import { ApplicationOverviewItemComponent } from "../../components/application-overview-item";
import { ActivatedRoute, Data, Router, RouterLink } from "@angular/router";
import { ICanComponentDeactivate } from "../../routing/guards/page/i-can-component-deactivate";
import { SanitizeHtmlPipe, ZTemplateDirective } from '@zaeper/angular-core-lib';
import { appRestClientInjectionToken } from "../../rest-clients/application/app/app-rest-client-injection-token";
import { IAppRestClient } from "../../rest-clients/application/app/i-app-rest.client";
import { IApp } from "../../models/application/i-app";
import { IImageUploadResponse, IPage } from '@zaeper/communication-lib';
import {
  appVersionRestClientInjectionToken
} from "../../rest-clients/application-version/app-version/app-version-rest-client-injection-token";
import { IAppVersionRestClient } from "../../rest-clients/application-version/app-version/i-app-version-rest.client";
import { FileUploadModule } from "primeng/fileupload";
import { ColorPickerDirective } from "ngx-color-picker";
import { PopoverModule } from "primeng/popover";
import {
  createAppVersionModalServiceInjectionToken
} from "../../modals/create-app-version-modal/create-app-version-modal-service-injection-token";
import { CreateAppVersionModalService } from "../../modals/create-app-version-modal/create-app-version-modal.service";
import {
  ICreateAppVersionModalService
} from "../../modals/create-app-version-modal/i-create-app-version-modal.service";
import { IAppVersion } from "../../models/application-version/i-app-version";

@Component( {
  selector: 'app-detail-page',
  templateUrl: 'app-detail-page.component.html',
  imports: [
    PageLayoutComponent,
    ZTemplateDirective,
    InputGroupComponent,
    ButtonModule,
    SkeletonModule,
    TableModule,
    NgClass,
    ButtonModule,
    TabNavigationComponent,
    ApplicationOverviewItemComponent,
    CardComponent,
    NgOptimizedImage,
    FileUploadModule,
    ColorPickerDirective,
    PopoverModule,
    SanitizeHtmlPipe,
    Button,
    RouterLink
  ],
  providers: [
    {
      provide: modalServiceInjectionToken,
      useClass: ModalService
    }, {
      provide: createAppVersionModalServiceInjectionToken,
      useClass: CreateAppVersionModalService
    }, {
      provide: tabNavigationStateServiceInjectionToken,
      useClass: TabNavigationStateService
    }, {
      provide: confirmationModalServiceInjectionToken,
      useClass: ConfirmationModalService
    }, DialogService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class AppDetailPageComponent implements OnInit, OnDestroy, ICanComponentDeactivate {
  protected readonly ePageLayout = EPageLayout;
  protected readonly propertiesInputGroup: IInputGroupOptions;
  protected readonly localizedInfoInputGroup: IInputGroupOptions;
  protected readonly accessControlInputGroup: IInputGroupOptions;
  protected readonly detailTabs: ITab[];
  protected readonly appForm: FormGroup;
  protected readonly ETabNavigationMode = ETabNavigationMode;
  protected app$: WritableSignal<IApp | undefined> = signal( undefined );
  protected appVersions$: WritableSignal<IPage<IAppVersion> | undefined> = signal( undefined );
  private readonly _subscriptions: Subscription;

  constructor(
    @Inject( toastServiceInjectionToken ) private readonly _toastService: IToastService,
    @Inject( appRestClientInjectionToken ) private readonly _appRestClient: IAppRestClient,
    @Inject( appVersionRestClientInjectionToken ) private readonly _appVersionRestClient: IAppVersionRestClient,
    @Inject(
      confirmationModalServiceInjectionToken ) private readonly _confirmationModalService: IConfirmationModalService,
    @Inject(
      createAppVersionModalServiceInjectionToken ) private readonly _createAppVersionModalService: ICreateAppVersionModalService,
    private readonly _formBuilder: FormBuilder,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router
  ) {
    this.appForm = this._formBuilder.group( {
      name: new FormControl( "" ),
      identifier: new FormControl( "", {
        validators: [
          Validators.minLength( 2 ),
          Validators.maxLength( 63 ),
          Validators.pattern( /^[a-z0-9]+(-[a-z0-9]+)*$/ )
        ]
      } ),
      systemApp: new FormControl( false ),
      category: new FormControl( "" ),
      description: new FormControl( "" ),
      primaryHexColor: new FormControl( "" )
    } );

    this.detailTabs = this._createDetailTabs();
    this.localizedInfoInputGroup = this._createLocalizedInfoInputGroup();
    this.propertiesInputGroup = this._createPropertiesInputGroup();
    this.accessControlInputGroup = this._createAccessControlInputGroup();
    this._subscriptions = new Subscription();
  }

  canDeactivate(): boolean {
    if ( !this.appForm.dirty ) return true;
    return confirm( "Deine Änderungen werden nicht gespeichert. Seite trotzdem verlassen?" );
  };

  ngOnInit() {
    this._subscriptions.add( this._activatedRoute.data.subscribe( ( {
                                                                      app,
                                                                      appVersions
                                                                    }: Data ) => {
      this.app$.set( app );
      this.appVersions$.set( appVersions );

      this.appForm.patchValue( {
        name: app.name,
        identifier: app.identifier ?? "",
        systemApp: app.systemApp ?? false,
        category: "",
        description: app.description,
        primaryHexColor: app.primaryHexColor
      } );
    } ) );

    this._listenToAppFormChanges();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  @HostListener( 'window:beforeunload', [ '$event' ] ) handleBeforeUnload( event: BeforeUnloadEvent ): void {
    if ( this.appForm.dirty ) {
      event.preventDefault();
    }
  }

  public lazyLoadTable( tableLazyLoadEvent: TableLazyLoadEvent ): Promise<void> {
    return Promise.resolve();
  }

  protected async discardChanges(): Promise<void> {
    const shouldDiscardChanges: boolean = confirm( "Entwurf löschen?" );
    if ( shouldDiscardChanges ) {
      this.appForm.reset();
      await this._router.navigate( [ "de", "applications" ] );
    }
  }

  protected async uploadTitleImage( event: Event ): Promise<void> {
    if ( this.app$() === undefined ) return;

    const htmlInputElement: HTMLInputElement = <HTMLInputElement>event.target;

    if ( htmlInputElement.files && htmlInputElement.files.length ) {
      const file: File = htmlInputElement.files[ 0 ];

      try {
        const publicImageUrl: IImageUploadResponse = await this._appRestClient.uploadTitleImage( this.app$()!.id, {
          titleImage: file
        } );

        this.app$.update( ( app: IApp | undefined ): IApp => {
          return {
            ...app,
            titleImageUrl: publicImageUrl.imageUrl
          } as IApp;
        } );

        this._toastService.setMessage( {
          title: "Erfolgreich",
          detail: "Titelbild wurde erfolgreich hochgeladen.",
          severity: ESeverity.success
        } );
      } catch ( e ) {
        this._toastService.setMessage( {
          title: "Fehlgeschlagen",
          detail: "Titelbild konnte nicht hochgeladen werden.",
          severity: ESeverity.error
        } );
      }
    }
  }

  protected async uploadIconImage( event: Event, iconType: "light" | "dark" ): Promise<void> {
    if ( this.app$() === undefined ) return;

    const htmlInputElement: HTMLInputElement = <HTMLInputElement>event.target;

    if ( htmlInputElement.files && htmlInputElement.files.length ) {
      const file: File = htmlInputElement.files[ 0 ];

      try {
        const publicImageUrl: IImageUploadResponse = await this._appRestClient.uploadIconImage( this.app$()!.id, {
          iconImage: file,
          iconType
        } );

        this.app$.update( ( app: IApp | undefined ): IApp => {
          if ( iconType === "light" ) {
            return {
              ...app,
              lightIconUrl: publicImageUrl.imageUrl
            } as IApp;
          } else {
            return {
              ...app,
              darkIconUrl: publicImageUrl.imageUrl
            } as IApp;
          }
        } );

        this._toastService.setMessage( {
          title: "Erfolgreich",
          detail: "App Icon wurde erfolgreich hochgeladen.",
          severity: ESeverity.success
        } );
      } catch ( e ) {
        this._toastService.setMessage( {
          title: "Fehlgeschlagen",
          detail: "App Icon konnte nicht hochgeladen werden.",
          severity: ESeverity.error
        } );
      }
    }
  }

  protected async uploadLogoImage( event: Event, logoType: "light" | "dark" ): Promise<void> {
    if ( this.app$() === undefined ) return;

    const htmlInputElement: HTMLInputElement = <HTMLInputElement>event.target;

    if ( htmlInputElement.files && htmlInputElement.files.length ) {
      const file: File = htmlInputElement.files[ 0 ];

      try {
        const publicImageUrl: IImageUploadResponse = await this._appRestClient.uploadLogoImage( this.app$()!.id, {
          logoImage: file,
          logoType
        } );

        this.app$.update( ( app: IApp | undefined ): IApp => {
          if ( logoType === "light" ) {
            return {
              ...app,
              lightLogoUrl: publicImageUrl.imageUrl
            } as IApp;
          } else {
            return {
              ...app,
              darkLogoUrl: publicImageUrl.imageUrl
            } as IApp;
          }
        } );

        this._toastService.setMessage( {
          title: "Erfolgreich",
          detail: "App Logo wurde erfolgreich hochgeladen.",
          severity: ESeverity.success
        } );
      } catch ( e ) {
        this._toastService.setMessage( {
          title: "Fehlgeschlagen",
          detail: "App Logo konnte nicht hochgeladen werden.",
          severity: ESeverity.error
        } );
      }
    }
  }

  protected async update(): Promise<void> {
    const app: IApp | undefined = this.app$();

    if ( app === undefined ) return;

    try {
      await this._appRestClient.updateApp( app.id, this.appForm.value );
      this.appForm.reset( this.appForm.getRawValue() );

      this._toastService.setMessage( {
        title: "Erfolgreich",
        detail: "App wurde erfolgreich aktualisiert",
        severity: ESeverity.success
      } );
    } catch ( e ) {
      this._toastService.setMessage( {
        title: "Fehlgeschlagen",
        detail: "App konnte nicht aktualisiert werden",
        severity: ESeverity.error
      } );
    }
  }

  protected openCreateAppVersionModal(): void {
    const app: IApp | undefined = this.app$();

    if ( app === undefined ) return;

    const openModal = () => {
      this._createAppVersionModalService.open( {
        appId: app.id
      } );
    };

    if ( this.appForm.dirty ) {
      this._confirmationModalService.open( {
        title: 'Sie haben ungespeicherte Änderungen',
        description: 'Ihre Einstellungen enthalten nicht gespeicherte Änderungen. Änderungen zuerst speichern?',
        confirmationText: 'Speichern',
        onConfirm: async (): Promise<void> => {
          await this.update();
          openModal();
        }
      } );
    } else {
      openModal();
    }
  }

  protected deleteAppVersion( appVersion: IAppVersion ): void {
    const app: IApp | undefined = this.app$();
    if ( app === undefined ) return;

    this._confirmationModalService.open( {
      title: `Version "${ appVersion.versionNumber }" löschen`,
      description: `Möchten Sie die Version "${ appVersion.versionNumber }" unwiderruflich löschen?`,
      confirmationText: "Löschen",
      onConfirm: async (): Promise<void> => {
        try {
          await this._appVersionRestClient.deleteAppVersion( appVersion.id, app.id );

          this.appVersions$.update( ( page: IPage<IAppVersion> | undefined ): IPage<IAppVersion> | undefined => {
            if ( page === undefined ) return page;
            return {
              ...page,
              records: page.records.filter( ( v: IAppVersion ): boolean => v.id !== appVersion.id ),
              totalRecords: Math.max( 0, page.totalRecords - 1 )
            };
          } );

          this._toastService.setMessage( {
            title: "Erfolgreich",
            detail: "Version wurde gelöscht",
            severity: ESeverity.success
          } );
        } catch ( e ) {
          this._toastService.setMessage( {
            title: "Fehlgeschlagen",
            detail: "Version konnte nicht gelöscht werden",
            severity: ESeverity.error
          } );
        }
      }
    } );
  }

  protected async publish(): Promise<void> {
    const app: IApp | undefined = this.app$();

    if ( app === undefined ) return;

    try {
      await this._appRestClient.publishApp( app.id );

      this._toastService.setMessage( {
        title: "Erfolgreich",
        detail: "App wurde erfolgreich veröffentlicht",
        severity: ESeverity.success
      } );
    } catch ( e ) {
      this._toastService.setMessage( {
        title: "Fehlgeschlagen",
        detail: "App konnte nicht veröffentlicht werden",
        severity: ESeverity.error
      } );
    }
  }

  protected updatePrimaryHexColor( event: string ) {
    this.appForm.controls[ 'primaryHexColor' ].setValue( event );
  }

  private _listenToAppFormChanges() {
    this._subscriptions.add( this.appForm.valueChanges.subscribe( ( form ) => {
      this.app$.set( {
        ...this.app$()!,
        name: form.name,
        identifier: form.identifier || undefined,
        systemApp: !!form.systemApp,
        description: form.description,
        primaryHexColor: form.primaryHexColor
      } );
    } ) );
  }

  private _createDetailTabs(): ITab[] {
    return [
      {
        subject: 'English',
        title: 'English'
      }
    ];
  }

  private _createLocalizedInfoInputGroup(): IInputGroupOptions {
    return {
      groupId: "localized-info",
      children: [
        {
          fieldType: EFieldType.TEXTAREA,
          title: 'Beschreibung',
          inputId: 'description',
          placeholder: 'Beschreibung',
          required: false,
          formControlEntry: {
            formControl: this.appForm.get( "description" ) as FormControl<string | null>,
            key: "description"
          },
          wysiwygEditor: false
        }
      ]
    };
  }

  private _createPropertiesInputGroup(): IInputGroupOptions {
    return {
      groupId: "properties",
      title: "Eigenschaften",
      children: [
        {
          fieldType: EFieldType.TEXT,
          placeholder: "Name",
          required: true,
          formControlEntry: {
            formControl: this.appForm.controls[ "name" ] as FormControl<string | null>,
            key: "name"
          },
          title: "Name",
          inputId: "name"
        }, {
          fieldType: EFieldType.TEXT,
          placeholder: "e.g. my-app",
          required: false,
          formControlEntry: {
            formControl: this.appForm.controls[ "identifier" ] as FormControl<string | null>,
            key: "identifier"
          },
          title: "Identifier",
          description: "URL-safe slug used in the deployment path. Lowercase alphanumeric and hyphens only.",
          inputId: "identifier"
        }, {
          fieldType: EFieldType.BOOLEAN,
          placeholder: "System app",
          required: false,
          formControlEntry: {
            formControl: this.appForm.controls[ "systemApp" ] as FormControl<boolean | null>,
            key: "systemApp"
          },
          title: "System app",
          description: "Marks this as a Zaeper-built system app (vs. third-party). Affects the deployment URL prefix.",
          inputId: "system-app"
        }, {
          fieldType: EFieldType.SELECTION,
          placeholder: "Kategorie",
          required: true,
          formControlEntry: {
            formControl: this.appForm.controls[ "category" ] as FormControl<string | null>,
            key: "category"
          },
          selections: [
            {
              title: "Werkzeug",
              value: "tool"
            }, {
              title: "Spiele",
              value: "games"
            }
          ],
          title: "Kategorie",
          inputId: "category"
        },
      ]
    };
  }

  private _createAccessControlInputGroup(): IInputGroupOptions {
    return {
      groupId: "access-control",
      title: "Freigabeeinstellungen",
      children: [
        {
          fieldType: EFieldType.BOOLEAN,
          placeholder: "Öffentlich",
          required: true,
          formControlEntry: {
            formControl: new FormControl( true ),
            key: "is-public"
          },
          title: "Öffentlich",
          description: "Diese Einstellung legt fest, ob Ihre Applikation für alle im öffentlichen Store sichtbar ist oder nur für Sie bzw. ausgewählte Personen in der privaten Repository zugänglich sind.",
          inputId: "is-public"
        }
      ]
    };
  }
}
